import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { UserPlus, FileSpreadsheet, Upload, Download, CheckCircle2, Loader2, ArrowRight, User, Mail, Phone, PawPrint } from "lucide-react";

const PET_TYPES = ["Dog", "Cat", "Bird", "Reptile", "Horse", "Rabbit", "Other"];
const CSV_COLS = ["member_first_name", "member_last_name", "member_email", "member_phone", "member_address", "member_dob", "pet_name", "pet_type", "pet_dob", "notes"];

// Minimal CSV parser supporting quoted fields and commas inside quotes.
function parseCSV(text) {
  const rows = [];
  let row = [], field = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(c => (c || "").trim() !== ""));
}

const inputCls = "w-full px-4 py-3 rounded-xl border border-paw-border bg-white text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary transition";
const iconCls = "w-full pl-10 pr-4 py-3 rounded-xl border border-paw-border bg-white text-paw-text placeholder:text-paw-text-muted focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary transition";

const EMPTY = {
  member_first_name: "", member_last_name: "", member_email: "", member_phone: "",
  member_address: "", member_address_parts: null, member_dob: "",
  pet_name: "", pet_type: "Dog", pet_dob: "", notes: "",
};

export default function PartnerSubmit() {
  const [tab, setTab] = useState("single");
  return (
    <div className="max-w-[840px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-paw-navy">Submit a referral</h1>
      <p className="mt-2 text-paw-text-secondary">Refer a member and their pet. We invite them by email, they set a password, and we take it from there.</p>

      <div className="mt-6 inline-flex rounded-full border border-paw-border bg-white p-1">
        <button onClick={() => setTab("single")} className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "single" ? "bg-paw-primary text-white" : "text-paw-text-secondary"}`}><UserPlus className="w-4 h-4 inline mr-1.5 -mt-0.5" />Single</button>
        <button onClick={() => setTab("bulk")} className={`px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "bulk" ? "bg-paw-primary text-white" : "text-paw-text-secondary"}`}><FileSpreadsheet className="w-4 h-4 inline mr-1.5 -mt-0.5" />Bulk upload</button>
      </div>

      {tab === "single" ? <SingleForm /> : <BulkForm />}
    </div>
  );
}

function SingleForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.member_first_name || !form.member_email) { toast.error("Member first name and email are required."); return; }
    setLoading(true);
    try {
      await api.post("/partner/referrals", form);
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="mt-8 bg-white border border-paw-border rounded-3xl shadow-sm p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-paw-blue-50 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7 text-paw-primary" /></div>
        <h2 className="text-xl font-extrabold text-paw-navy">Referral submitted</h2>
        <p className="mt-2 text-paw-text-secondary">{form.member_first_name} has been invited at {form.member_email}.</p>
        <button onClick={() => { setForm(EMPTY); setDone(false); }} className="mt-6 inline-flex items-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-full px-6 py-3 font-semibold text-sm transition-all">Submit another <ArrowRight className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 bg-white border border-paw-border rounded-3xl shadow-sm p-6 sm:p-8 space-y-6">
      <div>
        <h3 className="text-sm font-extrabold tracking-[0.06em] uppercase text-paw-primary mb-3">Member</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="relative"><User className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" /><input className={iconCls} placeholder="First name" value={form.member_first_name} onChange={(e) => set("member_first_name", e.target.value)} required /></div>
          <input className={inputCls} placeholder="Last name" value={form.member_last_name} onChange={(e) => set("member_last_name", e.target.value)} />
          <div className="relative"><Mail className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" /><input type="email" className={iconCls} placeholder="Email" value={form.member_email} onChange={(e) => set("member_email", e.target.value)} required /></div>
          <div className="relative"><Phone className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" /><input className={iconCls} placeholder="Phone (optional)" value={form.member_phone} onChange={(e) => set("member_phone", e.target.value)} /></div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-paw-navy mb-1.5">Address (optional)</label>
          <AddressAutocomplete value={form.member_address} onChange={(v) => set("member_address", v)} onSelect={(parts) => setForm((p) => ({ ...p, member_address: parts.formatted, member_address_parts: parts }))} placeholder="Start typing the member's address" />
        </div>
      </div>
      <div>
        <h3 className="text-sm font-extrabold tracking-[0.06em] uppercase text-paw-primary mb-3">Pet</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="relative"><PawPrint className="w-4 h-4 text-paw-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" /><input className={iconCls} placeholder="Pet name" value={form.pet_name} onChange={(e) => set("pet_name", e.target.value)} /></div>
          <select className={inputCls} value={form.pet_type} onChange={(e) => set("pet_type", e.target.value)}>{PET_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input className={inputCls} placeholder="Pet birth year (optional)" value={form.pet_dob} onChange={(e) => set("pet_dob", e.target.value)} />
        </div>
      </div>
      <textarea rows={3} className={`${inputCls} resize-none`} placeholder="Notes (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
      <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-full px-7 py-3.5 font-semibold transition-all shadow-[0_8px_20px_-8px_rgba(15,98,214,0.6)]">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit referral <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function BulkForm() {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const csv = CSV_COLS.join(",") + "\n" + "Jane,Doe,jane@example.com,5555550123,\"123 Main St, Toronto\",1985,Rex,Dog,2019,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pawlinx_referrals_template.csv";
    a.click();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(String(reader.result || ""));
      if (parsed.length < 2) { toast.error("CSV needs a header row and at least one referral."); return; }
      const header = parsed[0].map((h) => h.trim().toLowerCase());
      const out = parsed.slice(1).map((r) => {
        const obj = {};
        header.forEach((h, i) => { if (CSV_COLS.includes(h)) obj[h] = (r[i] || "").trim(); });
        return obj;
      }).filter((o) => o.member_email);
      setRows(out);
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!rows.length) { toast.error("No valid rows to submit."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/partner/referrals/bulk", { referrals: rows });
      setResult(data);
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-8 bg-white border border-paw-border rounded-3xl shadow-sm p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 border-b border-paw-border">
        <div>
          <h3 className="font-bold text-paw-navy">Upload a CSV</h3>
          <p className="text-sm text-paw-text-secondary mt-0.5">Columns: first name, last name, email, phone, address, member birth year, pet name, pet type, pet birth year, notes.</p>
        </div>
        <button onClick={downloadTemplate} className="shrink-0 inline-flex items-center gap-2 border border-paw-border rounded-full px-4 py-2 text-sm font-semibold text-paw-navy hover:border-paw-text-muted"><Download className="w-4 h-4" /> Template</button>
      </div>

      <div className="mt-5">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-paw-border rounded-2xl py-10 flex flex-col items-center gap-2 text-paw-text-secondary hover:border-paw-primary hover:text-paw-primary transition-colors">
          <Upload className="w-7 h-7" />
          <span className="font-semibold">{fileName || "Choose a CSV file"}</span>
          <span className="text-xs">{rows.length ? `${rows.length} member${rows.length === 1 ? "" : "s"} ready` : "or download the template first"}</span>
        </button>
      </div>

      {rows.length > 0 && !result && (
        <div className="mt-5">
          <div className="max-h-56 overflow-y-auto rounded-xl border border-paw-border divide-y divide-paw-border">
            {rows.slice(0, 50).map((r, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-paw-navy truncate">{r.member_first_name} {r.member_last_name}</span>
                <span className="text-paw-text-muted truncate">{r.member_email}</span>
              </div>
            ))}
          </div>
          <button onClick={submit} disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-full px-7 py-3.5 font-semibold transition-all">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit {rows.length} referral{rows.length === 1 ? "" : "s"} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      )}

      {result && (
        <div className="mt-5 bg-paw-cream rounded-2xl p-5">
          <div className="flex items-center gap-2 text-paw-navy font-semibold"><CheckCircle2 className="w-5 h-5 text-paw-primary" /> {result.message}</div>
          {result.errors?.length > 0 && (
            <div className="mt-3 text-sm text-paw-text-secondary">
              <p className="font-semibold mb-1">Skipped rows:</p>
              <ul className="space-y-0.5">{result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
